import { GroupInfo, GroupParticipant, GroupMatch } from '@/hooks/useUserGroupInfo';

interface Team {
  teamOrder: number;
  name?: string;
  player1?: string;
  player2?: string;
  registrationId?: string;
}

interface ScheduleMatch {
  matchNumber: number;
  team1: string;
  team2: string;
  time: string;
  day: string;
}

interface GroupCalendar {
  group_name: string;
  category: string;
  subcategory?: string;
  day?: string; // "Jueves 30 de octubre"
  start_time?: string; // "18:00"
  court_name?: string;
  teams: Team[];
  schedule_matrix: {
    matches: ScheduleMatch[];
    teams?: Team[];
  };
}

interface Registration {
  id: string;
  user_id: string;
  partner_user_id: string | null;
  player1_name?: string;
  player2_name?: string;
}

/**
 * Parses group_calendars data to extract group-specific information
 */
export function parseGroupScheduleData(
  groupCalendar: GroupCalendar,
  currentUserId: string,
  registrations: Registration[]
): GroupInfo {
  console.log('[GroupParser] Processing group calendar:', {
    groupName: groupCalendar.group_name,
    teamsCount: groupCalendar.teams?.length || 0,
    matchesCount: groupCalendar.schedule_matrix?.matches?.length || 0
  });

  console.log('[GroupParser] Raw schedule_matrix:', {
    matchesCount: groupCalendar.schedule_matrix?.matches?.length,
    firstMatch: groupCalendar.schedule_matrix?.matches?.[0],
    teamsInMatrix: groupCalendar.schedule_matrix?.teams?.length
  });

  try {
    // Helper to check if a name is a placeholder
    const isPlaceholder = (name: string | undefined): boolean => {
      if (!name) return true;
      const normalized = name.toLowerCase().trim();
      return normalized === 'por asignar' || 
             normalized.includes('placeholder') ||
             normalized === '';
    };

    // Helper to get real names - prioritize registrations data
    const getTeamNames = (team: Team): { player1: string; player2?: string } => {
      // First, try to find the registration for this team
      if (team.registrationId) {
        const reg = registrations.find(r => r.id === team.registrationId);
        if (reg?.player1_name && !isPlaceholder(reg.player1_name)) {
          console.log('[GroupParser] Using registration names for team:', {
            registrationId: team.registrationId,
            player1: reg.player1_name,
            player2: reg.player2_name
          });
          return {
            player1: reg.player1_name,
            player2: reg.player2_name || undefined
          };
        }
      }

      // Fallback: parse from team.name or team.player1
      const teamName = team.name || team.player1 || '';
      
      // If teamName is a placeholder, try to find by teamOrder
      if (isPlaceholder(teamName)) {
        // Try to match by order in registrations array
        const regByOrder = registrations[team.teamOrder - 1];
        if (regByOrder?.player1_name && !isPlaceholder(regByOrder.player1_name)) {
          console.log('[GroupParser] Using registration by order for team:', {
            teamOrder: team.teamOrder,
            player1: regByOrder.player1_name,
            player2: regByOrder.player2_name
          });
          return {
            player1: regByOrder.player1_name,
            player2: regByOrder.player2_name || undefined
          };
        }
        return { player1: 'Por asignar', player2: undefined };
      }

      // Parse "Player1 / Player2" format
      const names = teamName.split(' / ');
      return {
        player1: names[0]?.trim() || 'Por asignar',
        player2: names[1]?.trim()
      };
    };

    // Prioritize teams from the top-level (usually more up-to-date)
    const teamsSource = groupCalendar.teams?.length > 0 
      ? groupCalendar.teams 
      : groupCalendar.schedule_matrix?.teams || [];

    // Build participants list from teams
    const participants: GroupParticipant[] = teamsSource
      .sort((a, b) => a.teamOrder - b.teamOrder)
      .map((team) => {
        const { player1, player2 } = getTeamNames(team);
        
        // Check if this team includes the current user
        const isUser = registrations.some(reg => 
          reg.id === team.registrationId && 
          (reg.user_id === currentUserId || reg.partner_user_id === currentUserId)
        );

        return {
          id: team.registrationId || `team-${team.teamOrder}`,
          letter: String.fromCharCode(65 + team.teamOrder - 1), // teamOrder 1 = A
          player1,
          player2,
          isUser
        };
      });

    // Helper function to normalize team names for comparison
    const normalizeTeamName = (name: string): string => {
      return name
        .trim()                          // Remove leading/trailing spaces
        .toLowerCase()                   // Case-insensitive FIRST
        .replace(/\s*\/\s*$/g, '')      // Remove trailing " / " or "/" (GLOBAL)
        .replace(/\/\s*$/g, '')         // Remove trailing "/" without space (GLOBAL)
        .replace(/\s*\/\s*/g, ' / ')    // Normalize ALL " / " to consistent format (GLOBAL)
        .replace(/\s+/g, ' ')           // Normalize multiple spaces to single space
        .trim();                         // Final trim after all transformations
    };

    // Parse matches from schedule_matrix - sort by matchNumber for deterministic order
    const sortedMatches = [...(groupCalendar.schedule_matrix?.matches || [])].sort(
      (a, b) => (Number(a.matchNumber) || 0) - (Number(b.matchNumber) || 0)
    );
    
    console.log('[GroupParser] 🔍 Sorted matches input:', {
      groupName: groupCalendar.group_name,
      category: groupCalendar.category,
      participantOrder: participants.map(p => ({ letter: p.letter, name: `${p.player1}${p.player2 ? '/' + p.player2 : ''}`, isUser: p.isUser, regId: p.id })),
      matchesInput: sortedMatches.map(m => ({ num: m.matchNumber, time: m.time, t1: m.team1, t2: m.team2 }))
    });

    // Always match by team name (not by hardcoded matchNumber pattern).
    // This ensures correctness even when participants array order doesn't follow standard A vs B / A vs C / B vs C pattern.
    const matches: GroupMatch[] = sortedMatches.map(match => {
      const team1Participant = participants.find(p => {
        const fullName = `${p.player1}${p.player2 ? ' / ' + p.player2 : ''}`;
        return normalizeTeamName(fullName) === normalizeTeamName(match.team1);
      });

      const team2Participant = participants.find(p => {
        if (p.id === team1Participant?.id) return false;
        const fullName = `${p.player1}${p.player2 ? ' / ' + p.player2 : ''}`;
        return normalizeTeamName(fullName) === normalizeTeamName(match.team2);
      });

      console.log('[GroupParser] 🎯 Match resolved by name:', {
        matchNumber: match.matchNumber,
        time: match.time,
        team1Name: match.team1,
        team1Letter: team1Participant?.letter,
        team1IsUser: team1Participant?.isUser,
        team2Name: match.team2,
        team2Letter: team2Participant?.letter,
        team2IsUser: team2Participant?.isUser
      });

      if (!team1Participant || !team2Participant) {
        console.warn('[GroupParser] ⚠️ Could not resolve teams by name for match:', {
          matchNumber: match.matchNumber,
          team1: match.team1,
          team2: match.team2,
          availableParticipants: participants.map(p => `${p.letter}: ${p.player1}${p.player2 ? ' / ' + p.player2 : ''}`)
        });
      }

      return {
        id: `${groupCalendar.group_name}-${match.matchNumber}`,
        matchId: `${groupCalendar.group_name}-${match.team1}-vs-${match.team2}`,
        participants: [
          team1Participant?.letter || 'A',
          team2Participant?.letter || 'B'
        ] as [string, string],
        time: match.time,
        date: groupCalendar.day || match.day || '',
        court: groupCalendar.court_name
      };
    });

    // Calculate end time (assuming 3 hours of play if not specified)
    let endTime = '';
    if (groupCalendar.start_time) {
      const [hours, minutes] = groupCalendar.start_time.split(':').map(Number);
      const endHours = hours + 3;
      endTime = `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    const groupInfo: GroupInfo = {
      groupName: groupCalendar.group_name,
      category: groupCalendar.category,
      subcategory: groupCalendar.subcategory,
      date: groupCalendar.day || '',
      startTime: groupCalendar.start_time || '',
      endTime: endTime,
      court: groupCalendar.court_name,
      participants,
      matches
    };

    console.log('[GroupParser] ✅ Processed group info:', {
      groupName: groupInfo.groupName,
      participantsCount: participants.length,
      matchesCount: matches.length
    });

    return groupInfo;
  } catch (error) {
    console.error('[GroupParser] ❌ Error processing group calendar:', error);
    
    // Return minimal valid group info
    return {
      groupName: groupCalendar.group_name,
      category: groupCalendar.category || '',
      subcategory: groupCalendar.subcategory,
      date: groupCalendar.day || '',
      startTime: groupCalendar.start_time || '',
      endTime: '',
      court: groupCalendar.court_name,
      participants: [],
      matches: []
    };
  }
}

/**
 * Formats a date string to a readable format
 * @param dateStr - Date string in format "2025-10-24"
 * @returns Formatted date like "Viernes - 24 de octubre"
 */
export function formatScheduleDate(dateStr: string): string {
  if (!dateStr) return '';
  
  try {
    const date = new Date(dateStr + 'T00:00:00');
    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const monthNames = [
      'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];
    
    const dayName = dayNames[date.getDay()];
    const day = date.getDate();
    const monthName = monthNames[date.getMonth()];
    
    return `${dayName} - ${day} de ${monthName}`;
  } catch (error) {
    return dateStr;
  }
}
